/** XeAgents.ai**/
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, CreditCard, Settings, BarChart, Phone, Package, Bell, ListOrdered, Loader2, CheckCircle2, XCircle, ContactRound, DollarSign, RefreshCw, Server, Receipt, Mail, MessageSquare, Headphones, ShieldAlert, Stethoscope, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import UserManagement from "@/components/admin/UserManagement";
import PlanAndTopupManagement from "@/components/admin/PlanAndTopupManagement";
import LLMModelsManagement from "@/components/admin/LLMModelsManagement";
import SettingsPage from "@/components/admin/SettingsPage";
import Analytics from "@/components/admin/Analytics";
import PhoneNumbers from "@/components/admin/PhoneNumbers";
import Notifications from "@/components/admin/Notifications";
import BatchJobsMonitor from "@/components/admin/BatchJobsMonitor";
import AllContactsAdmin from "@/components/admin/AllContactsAdmin";
import PaymentsSettings from "@/components/admin/PaymentsSettings";
import TransactionsManagement from "@/components/admin/TransactionsManagement";
import EmailSettingsManagement from "@/components/admin/EmailSettingsManagement";
import CallMonitoring from "@/components/admin/CallMonitoring";
import BannedWordsManagement from "@/components/admin/BannedWordsManagement";
import UserDiagnostics from "@/components/admin/UserDiagnostics";
import PilotRequests from "@/components/admin/PilotRequests";
import { Badge } from "@/components/ui/badge";
import { apiRequest } from "@/lib/queryClient";
import { AuthStorage } from "@/lib/auth-storage";

interface ConnectionStatus {
  connected: boolean;
  error?: string;
  accountName?: string;
  accountStatus?: string;
  voiceCount?: number;
  details?: string;
}

export default function AdminDashboard() {
  const [location] = useLocation();
  const [activeTab, setActiveTab] = useState("analytics");
  // Set when a pilot request row is clicked - carries the id across to the
  // owner onboarding form on the Settings tab.
  const [pilotRequestToOnboard, setPilotRequestToOnboard] = useState<string | null>(null);
  const [twilioStatus, setTwilioStatus] = useState<ConnectionStatus | null>(null);
  const [elevenLabsStatus, setElevenLabsStatus] = useState<ConnectionStatus | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Get analytics summary
  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ["/api/admin/analytics"],
  });

  // Get global settings to check configuration
  // Auto-refresh every 60 minutes and on window focus to keep connection status current
  const { data: settings, isLoading: settingsLoading, refetch: refetchSettings, isFetching } = useQuery({
    queryKey: ["/api/admin/settings"],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/admin/settings?t=${Date.now()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }
      return response.json();
    },
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 60 * 1000, // 60 minutes
    staleTime: 0,
  });

  // Promise chain for serialized connection checks - ensures every trigger runs in order
  const checkChainRef = useRef<Promise<void>>(Promise.resolve());
  const wasFetchingRef = useRef(false);
  const hasCheckedRef = useRef(false);

  // Perform the actual connection test - internal function
  const performConnectionCheck = async (settingsToUse: any): Promise<void> => {
    setCheckingStatus(true);
    
    try {
      // Test Twilio connection
      if (settingsToUse?.twilio_configured) {
        try {
          const twilioResponse = await apiRequest("POST", "/api/admin/test-connection/twilio");
          const twilioResult = await twilioResponse.json();
          setTwilioStatus(twilioResult as ConnectionStatus);
        } catch (err) {
          setTwilioStatus({ connected: false, error: `Test failed: ${err instanceof Error ? err.message : 'Unknown error'}` });
        }
      } else {
        setTwilioStatus({ connected: false, error: 'Not configured' });
      }

      // Test ElevenLabs connection
      if (settingsToUse?.elevenlabs_configured) {
        try {
          const elevenLabsResponse = await apiRequest("POST", "/api/admin/test-connection/elevenlabs");
          const elevenLabsResult = await elevenLabsResponse.json();
          setElevenLabsStatus(elevenLabsResult as ConnectionStatus);
        } catch (err) {
          setElevenLabsStatus({ connected: false, error: `Test failed: ${err instanceof Error ? err.message : 'Unknown error'}` });
        }
      } else {
        setElevenLabsStatus({ connected: false, error: 'Not configured' });
      }
    } catch (error) {
      console.error('Error checking API connections:', error);
      setTwilioStatus({ connected: false, error: 'Check failed' });
      setElevenLabsStatus({ connected: false, error: 'Check failed' });
    } finally {
      setCheckingStatus(false);
    }
  };

  // Queue a connection check - chains onto existing promise to ensure serial execution
  const checkAPIConnections = (currentSettings?: any) => {
    const settingsToUse = currentSettings || settings;
    if (!settingsToUse) return;
    
    // Chain this check onto the existing promise chain
    checkChainRef.current = checkChainRef.current
      .then(() => performConnectionCheck(settingsToUse))
      .catch((err) => {
        console.error('Connection check chain error:', err);
        // Reset error states on chain failure
        setTwilioStatus({ connected: false, error: 'Check failed' });
        setElevenLabsStatus({ connected: false, error: 'Check failed' });
        setCheckingStatus(false);
      });
  };

  // Manual refresh handler for recovery
  const handleManualRefresh = async () => {
    await refetchSettings();
  };

  // Force refresh connection status when navigating to /admin
  useEffect(() => {
    if (location?.startsWith('/admin')) {
      refetchSettings();
    }
  }, [location]);

  // Check connections when settings fetch completes (for route navigation, 60-min interval, and window focus)
  useEffect(() => {
    if (wasFetchingRef.current && !isFetching && settings) {
      // Fetch just completed, check connections with fresh data
      checkAPIConnections(settings);
    }
    wasFetchingRef.current = isFetching;
  }, [isFetching, settings]);

  // Initial check when settings first loads
  useEffect(() => {
    if (settings && !hasCheckedRef.current) {
      hasCheckedRef.current = true;
      checkAPIConnections(settings);
    }
    // Reset flag if settings becomes unavailable (e.g., after error)
    if (!settings) {
      hasCheckedRef.current = false;
    }
  }, [settings]);

  return (
    <div className="p-4 md:p-8 space-y-6 md:space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold">Platform Analytics</h1>
          <p className="text-muted-foreground mt-1 md:mt-2 text-sm md:text-base">
            Monitor platform performance and system health
          </p>
        </div>
        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          {checkingStatus ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Checking...</span>
            </div>
          ) : (
            <>
              <div 
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                  twilioStatus?.connected 
                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' 
                    : 'bg-slate-50 dark:bg-slate-900/30 border-slate-200 dark:border-slate-700'
                }`}
                data-testid={twilioStatus?.connected ? "status-twilio-connected" : "status-twilio-disconnected"}
                title={twilioStatus?.error || undefined}
              >
                <div className={`p-1 rounded-full ${
                  twilioStatus?.connected 
                    ? 'bg-emerald-500' 
                    : 'bg-slate-400'
                }`}>
                  <Phone className="h-3 w-3 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className={`text-xs font-medium ${
                    twilioStatus?.connected 
                      ? 'text-emerald-700 dark:text-emerald-400' 
                      : 'text-slate-600 dark:text-slate-400'
                  }`}>
                    Twilio
                  </span>
                  <span className={`text-[10px] ${
                    twilioStatus?.connected 
                      ? 'text-emerald-600/70 dark:text-emerald-500/70' 
                      : 'text-slate-500 dark:text-slate-500'
                  }`}>
                    {twilioStatus?.connected ? 'Connected' : 'Not Connected'}
                  </span>
                </div>
              </div>
              
              <div 
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                  elevenLabsStatus?.connected 
                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' 
                    : 'bg-slate-50 dark:bg-slate-900/30 border-slate-200 dark:border-slate-700'
                }`}
                data-testid={elevenLabsStatus?.connected ? "status-elevenlabs-connected" : "status-elevenlabs-disconnected"}
                title={elevenLabsStatus?.error || undefined}
              >
                <div className={`p-1 rounded-full ${
                  elevenLabsStatus?.connected 
                    ? 'bg-emerald-500' 
                    : 'bg-slate-400'
                }`}>
                  <Server className="h-3 w-3 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className={`text-xs font-medium ${
                    elevenLabsStatus?.connected 
                      ? 'text-emerald-700 dark:text-emerald-400' 
                      : 'text-slate-600 dark:text-slate-400'
                  }`}>
                    ElevenLabs
                  </span>
                  <span className={`text-[10px] ${
                    elevenLabsStatus?.connected 
                      ? 'text-emerald-600/70 dark:text-emerald-500/70' 
                      : 'text-slate-500 dark:text-slate-500'
                  }`}>
                    {elevenLabsStatus?.connected 
                      ? elevenLabsStatus.voiceCount !== undefined 
                        ? `${elevenLabsStatus.voiceCount} voices` 
                        : 'Connected'
                      : 'Not Connected'}
                  </span>
                </div>
              </div>
              
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleManualRefresh}
                disabled={checkingStatus || isFetching}
                title="Refresh connection status"
                data-testid="button-refresh-status"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Main Admin Tabs - Menu first */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="flex gap-1 h-auto w-max md:w-full">
            <TabsTrigger value="analytics" className="text-xs md:text-sm whitespace-nowrap" data-testid="tab-analytics">
              <BarChart className="h-4 w-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Analytics</span>
              <span className="sm:hidden">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="text-xs md:text-sm whitespace-nowrap" data-testid="tab-users">
              <Users className="h-4 w-4 mr-1 md:mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="contacts" className="text-xs md:text-sm whitespace-nowrap" data-testid="tab-contacts">
              <ContactRound className="h-4 w-4 mr-1 md:mr-2" />
              Contacts
            </TabsTrigger>
            <TabsTrigger value="billing" className="text-xs md:text-sm whitespace-nowrap" data-testid="tab-billing">
              <CreditCard className="h-4 w-4 mr-1 md:mr-2" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="phones" className="text-xs md:text-sm whitespace-nowrap" data-testid="tab-phones">
              <Phone className="h-4 w-4 mr-1 md:mr-2" />
              Phones
            </TabsTrigger>
            <TabsTrigger value="queue" className="text-xs md:text-sm whitespace-nowrap" data-testid="tab-batch-jobs">
              <ListOrdered className="h-4 w-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Batch Jobs</span>
              <span className="sm:hidden">Jobs</span>
            </TabsTrigger>
            <TabsTrigger value="calls" className="text-xs md:text-sm whitespace-nowrap" data-testid="tab-calls">
              <Headphones className="h-4 w-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Call Monitoring</span>
              <span className="sm:hidden">Calls</span>
            </TabsTrigger>
            <TabsTrigger value="communications" className="text-xs md:text-sm whitespace-nowrap" data-testid="tab-communications">
              <MessageSquare className="h-4 w-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Communications</span>
              <span className="sm:hidden">Comms</span>
            </TabsTrigger>
            <TabsTrigger value="pilot-requests" className="text-xs md:text-sm whitespace-nowrap" data-testid="tab-pilot-requests">
              <Rocket className="h-4 w-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Pilot Requests</span>
              <span className="sm:hidden">Pilots</span>
            </TabsTrigger>
            <TabsTrigger value="diagnostics" className="text-xs md:text-sm whitespace-nowrap" data-testid="tab-diagnostics">
              <Stethoscope className="h-4 w-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Diagnostics</span>
              <span className="sm:hidden">Diag</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="text-xs md:text-sm whitespace-nowrap" data-testid="tab-settings">
              <Settings className="h-4 w-4 mr-1 md:mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="analytics" className="space-y-4">
          <Analytics />
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <UserManagement />
        </TabsContent>

        <TabsContent value="contacts" className="space-y-4">
          <AllContactsAdmin />
        </TabsContent>

        <TabsContent value="billing" className="space-y-4">
          <BillingPanel />
        </TabsContent>

        <TabsContent value="phones" className="space-y-4">
          <PhoneNumbers />
        </TabsContent>

        <TabsContent value="queue" className="space-y-4">
          <BatchJobsMonitor />
        </TabsContent>

        <TabsContent value="calls" className="space-y-4">
          <CallsPanel />
        </TabsContent>

        <TabsContent value="communications" className="space-y-4">
          <CommunicationsPanel />
        </TabsContent>

        <TabsContent value="pilot-requests" className="space-y-4">
          <PilotRequests
            onOnboard={(id) => {
              setPilotRequestToOnboard(id);
              setActiveTab("settings");
            }}
          />
        </TabsContent>

        <TabsContent value="diagnostics" className="space-y-4">
          <UserDiagnostics />
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <SettingsPage
            onSwitchTab={setActiveTab}
            initialSubTab={pilotRequestToOnboard ? "onboarding" : undefined}
            pilotRequestToOnboard={pilotRequestToOnboard}
            onPilotRequestConsumed={() => setPilotRequestToOnboard(null)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BillingPanel() {
  const [activeSubTab, setActiveSubTab] = useState("plans");
  const isSuperAdmin = AuthStorage.isSuperAdmin();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Billing</h2>
        <p className="text-muted-foreground">
          Manage subscription plans, credit topups, transactions, and payment gateways
        </p>
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList>
          <TabsTrigger value="plans" data-testid="subtab-plans">
            <Package className="h-4 w-4 mr-2" />
            Plans & Topups
          </TabsTrigger>
          <TabsTrigger value="llm" data-testid="subtab-llm">
            <Settings className="h-4 w-4 mr-2" />
            LLM Models
          </TabsTrigger>
          <TabsTrigger value="transactions" data-testid="subtab-transactions">
            <Receipt className="h-4 w-4 mr-2" />
            Transactions
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="payments" data-testid="subtab-payments">
              <DollarSign className="h-4 w-4 mr-2" />
              Payments
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="plans" className="mt-6">
          <PlanAndTopupManagement />
        </TabsContent>

        <TabsContent value="llm" className="mt-6">
          <LLMModelsManagement />
        </TabsContent>

        <TabsContent value="transactions" className="mt-6">
          <TransactionsManagement />
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="payments" className="mt-6">
            <PaymentsSettings />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function CallsPanel() {
  const [activeSubTab, setActiveSubTab] = useState("monitoring");
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Call Monitoring</h2>
        <p className="text-muted-foreground">
          Monitor all calls, detect content violations, and manage banned words
        </p>
      </div>
      
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList>
          <TabsTrigger value="monitoring" data-testid="subtab-call-monitoring">
            <Headphones className="h-4 w-4 mr-2" />
            All Calls
          </TabsTrigger>
          <TabsTrigger value="banned-words" data-testid="subtab-banned-words">
            <ShieldAlert className="h-4 w-4 mr-2" />
            Banned Words
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="monitoring" className="mt-6">
          <CallMonitoring />
        </TabsContent>
        
        <TabsContent value="banned-words" className="mt-6">
          <BannedWordsManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CommunicationsPanel() {
  const [activeSubTab, setActiveSubTab] = useState("email-settings");
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Communications</h2>
        <p className="text-muted-foreground">
          Manage email templates, notifications, and communication settings
        </p>
      </div>
      
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList>
          <TabsTrigger value="email-settings" data-testid="subtab-email-settings">
            <Mail className="h-4 w-4 mr-2" />
            Email Settings
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="subtab-notifications">
            <Bell className="h-4 w-4 mr-2" />
            In-App Notifications
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="email-settings" className="mt-6">
          <EmailSettingsManagement />
        </TabsContent>
        
        <TabsContent value="notifications" className="mt-6">
          <Notifications />
        </TabsContent>
      </Tabs>
    </div>
  );
}